import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useFunction } from '@/hooks/useFunction';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Empty } from '@/components/ui/empty';
import { ListSkeleton } from '@/components/PageSkeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Busy, ErrorState, Field } from '@/components/kit';
import { FormDialog, PickSelect, SwitchRow } from '@/components/inputs';
import CopyButton from '@/components/admin/CopyButton';
import { orderVars, renderTemplate, templatesOf } from '@/shared/messages';
import { committed, logActivity, nowIso } from '@/shared/data';
import { syncHandle } from '@/shared/referrals';

/** Edit the line credentials on an order. */
export function LineDialog({ open, onOpenChange, order, onSaved }) {
  const [form, setForm] = useState({ server: '', username: '', password: '', epg: false, note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        server: order.line_server_c ?? '',
        username: order.line_username_c ?? '',
        password: order.line_password_c ?? '',
        epg: !!order.include_epg_c,
        note: order.line_note_c ?? '',
      });
    }
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function submit() {
    if (!form.username.trim()) {
      toast.error('Add at least the line username');
      return;
    }
    setSaving(true);
    try {
      const res = await sdk.table('orders_c').update({
        Id: order.Id,
        line_server_c: form.server.trim(),
        line_username_c: form.username.trim(),
        line_password_c: form.password.trim(),
        include_epg_c: !!form.epg,
        line_note_c: form.note.trim(),
      });
      if (!committed(res, 'Line details saved')) return;
      syncHandle(order);
      onOpenChange(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Line details"
      description="The customer sees these on their order page once saved."
      onSubmit={submit}
      saving={saving}
      submitLabel="Save line"
    >
      <Field label="Server URL" htmlFor="line-server">
        <Input
          id="line-server"
          value={form.server}
          onChange={(e) => set('server')(e.target.value)}
          placeholder="http://line.ghostcable.app:8080"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Username" htmlFor="line-user">
          <Input id="line-user" value={form.username} onChange={(e) => set('username')(e.target.value)} placeholder="maya4413" />
        </Field>
        <Field label="Password" htmlFor="line-pass">
          <Input id="line-pass" value={form.password} onChange={(e) => set('password')(e.target.value)} placeholder="k7Qp2vXz" />
        </Field>
      </div>
      <SwitchRow
        id="line-epg"
        label="Premium TV guide (EPG)"
        hint="Included with 6 and 12 month Wraith packages."
        checked={form.epg}
        onChange={set('epg')}
      />
      <Field label="Note for the customer" htmlFor="line-note">
        <Textarea
          id="line-note"
          rows={3}
          value={form.note}
          onChange={(e) => set('note')(e.target.value)}
          placeholder="Use the M3U link in TiviMate, restart the app after adding…"
        />
      </Field>
    </FormDialog>
  );
}

/** Save the Desktop Telly licence key(s) and optionally mark them as sent. */
export function TellyKeyDialog({ open, onOpenChange, order, onSaved }) {
  const [key, setKey] = useState('');
  const [markSent, setMarkSent] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setKey(order.telly_key_c ?? '');
      setMarkSent(!order.telly_sent_at_c);
    }
  }, [open]);

  async function submit() {
    if (!key.trim()) {
      toast.error('Paste the licence key first');
      return;
    }
    setSaving(true);
    try {
      const res = await sdk.table('orders_c').update({
        Id: order.Id,
        telly_key_c: key.trim(),
        telly_sent_at_c: markSent ? nowIso() : order.telly_sent_at_c ?? null,
      });
      if (!committed(res, 'Licence key saved')) return;
      if (markSent) logActivity('Email', 'Sent Desktop Telly key', order.Name, order.email_c);
      onOpenChange(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Desktop Telly licence"
      description="One key per line if the order has several licences."
      onSubmit={submit}
      saving={saving}
      submitLabel="Save key"
    >
      <Field label="Licence key" htmlFor="telly-key">
        <Textarea
          id="telly-key"
          rows={4}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="DTLY-4K9Q-7MZP-2RXV"
          className="font-mono"
        />
      </Field>
      <SwitchRow
        id="telly-sent"
        label="Mark as sent now"
        hint="Stamps the order so you know the customer has it."
        checked={markSent}
        onChange={setMarkSent}
      />
    </FormDialog>
  );
}

function CopyBlock({ label, text, mono = false }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <CopyButton text={text} label={label} size="sm" variant="ghost" />
      </div>
      <pre
        className={
          mono
            ? 'overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 font-mono text-sm'
            : 'overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 font-sans text-sm'
        }
      >
        {text || ' '}
      </pre>
    </div>
  );
}

/**
 * Render a message template for an order so it can be copied or opened in an email app.
 * With `stampField`, "Mark as sent" stamps that order field.
 */
export function MessageDialog({ open, onOpenChange, order, kind, title, description, stampField, onSent }) {
  const navigate = useNavigate();
  const templates = useFetch(() => (open ? templatesOf(kind) : Promise.resolve([])), [open, kind]);
  const [picked, setPicked] = useState('');
  const [saving, setSaving] = useState(false);
  const mailer = useFunction(import.meta.env.VITE_SEND_EMAIL);
  const list = templates.data ?? [];
  const template = list.find((t) => String(t.Id) === picked) ?? list[0];
  const message = template ? renderTemplate(template, orderVars(order)) : null;
  const email = order?.email_c ?? '';
  const mailto = message
    ? `mailto:${email}?subject=${encodeURIComponent(message.subject)}&body=${encodeURIComponent(message.body)}`
    : undefined;

  async function markSent() {
    setSaving(true);
    try {
      const res = await sdk.table('orders_c').update({ Id: order.Id, [stampField]: nowIso() });
      if (!committed(res, 'Marked as sent')) return;
      logActivity('Email', title, order.Name, email);
      onOpenChange(false);
      onSent?.();
    } finally {
      setSaving(false);
    }
  }

  async function sendNow() {
    if (!message || !email) return;
    const res = await mailer.invoke({ mode: 'mail', to: email, subject: message.subject, text: message.body });
    if (!res) return;
    if (!res.sent || res.partial) {
      toast.error(res.message || 'The email did not go out');
      return;
    }
    if (stampField) {
      const stamped = await sdk.table('orders_c').update({ Id: order.Id, [stampField]: nowIso() });
      if (!committed(stamped)) toast.warning('Sent, but the order was not marked as sent');
    }
    logActivity('Email', title, order.Name, email);
    toast.success(`Sent to ${email}`);
    onOpenChange(false);
    onSent?.();
  }

  const busy = saving || mailer.loading;

  let body;
  if (templates.loading && templates.data === undefined) {
    body = <ListSkeleton rows={3} />;
  } else if (templates.error) {
    body = <ErrorState message={templates.error} onRetry={templates.run} />;
  } else if (!message) {
    body = (
      <Empty
        icon="FileText"
        title={`No ${kind.toLowerCase()} template yet`}
        description="Write one under Templates and it shows up here, filled in for this order."
        action={() => navigate('/admin/templates')}
        actionLabel="Open templates"
      />
    );
  } else {
    body = (
      <div className="space-y-4">
        {list.length > 1 && (
          <Field label="Template" htmlFor="msg-template">
            <PickSelect
              id="msg-template"
              value={String(template.Id)}
              onChange={setPicked}
              values={list.map((t) => String(t.Id))}
              labels={Object.fromEntries(list.map((t) => [String(t.Id), t.Name]))}
              placeholder="Choose a template"
            />
          </Field>
        )}
        <CopyBlock label="To" text={email} mono />
        <CopyBlock label="Subject" text={message.subject} />
        <CopyBlock label="Message" text={message.body} />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {body}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Close
          </Button>
          {mailto && (
            <Button type="button" variant="outline" asChild>
              <a href={mailto}>
                <ApperIcon name="Mail" size={16} />
                Open in email app
              </a>
            </Button>
          )}
          {stampField && message && (
            <Button type="button" variant="outline" onClick={markSent} disabled={busy}>
              <Busy busy={saving} label="Mark as sent" busyLabel="Saving…" />
            </Button>
          )}
          {message && email && (
            <Button type="button" onClick={sendNow} disabled={busy}>
              <Busy
                busy={mailer.loading}
                label={
                  <>
                    <ApperIcon name="Send" size={16} />
                    Send email
                  </>
                }
                busyLabel="Sending…"
              />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
